import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Button from 'common/components/button/Button';
import InputGroup from 'common/components/inputGroup/InputGroup';
import styles from './ThresholdModal.module.scss';

export type ThresholdField = {
    key: string;
    label: string;
    unit: string;
    value: number;
    min?: number;
    // Also blocks typing above it
    max?: number;
    // Decimal places allowed (default: integers only)
    decimals?: number;
};

type Props = {
    fields: ThresholdField[];
    description?: string;
    onApply: (values: Record<string, number>) => void;
    onClose: () => void;
};

// Small modal to edit one or more numeric thresholds ("alert in X days", "X% before", ...).
// Rendered in a portal so it isn't trapped inside transformed parents (dashboard grid items).
const ThresholdModal = ({ fields, description, onApply, onClose }: Props) => {
    const { t } = useTranslation();
    const [values, setValues] = useState<Record<string, number | undefined>>(() =>
        Object.fromEntries(fields.map(f => [f.key, f.value])));

    // Invalid or out-of-range values keep the previous threshold
    const apply = () => {
        const result: Record<string, number> = {};
        fields.forEach(f => {
            const val = values[f.key];
            const isValid = val !== undefined && val >= (f.min ?? 1) && (f.max === undefined || val <= f.max);
            result[f.key] = isValid ? val as number : f.value;
        });
        onApply(result);
    };

    return createPortal(
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                {fields.map((f, i) => (
                    <React.Fragment key={f.key}>
                        <label>{f.label}</label>
                        <InputGroup
                            type="number"
                            min={f.min ?? 1}
                            max={f.max}
                            maxValue={f.max}
                            value={values[f.key] ?? ''}
                            format={f.decimals ? 'number' : 'integer'}
                            numberDecimalCases={f.decimals}
                            noFixedDecimalCase
                            onValueChange={v => setValues(prev => ({ ...prev, [f.key]: typeof v === 'number' ? v : undefined }))}
                            onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                            text={f.unit}
                            autoFocus={i === 0}
                        />
                    </React.Fragment>
                ))}
                {description && <p className={styles.description}>{description}</p>}
                <Button text={t('common.apply') as string} preset="primary" onClick={apply} bw={false} />
            </div>
        </div>,
        document.body
    );
};

export default ThresholdModal;
